import { useEffect, useState } from "react";
import { api, API } from "@/lib/api";
import { toast } from "sonner";
import {
  Loader2, Users, Bot, MessageSquare, UserPlus, IndianRupee, TrendingUp,
  X, Crown, Mail, Phone, Search, Plus, Trash2, AlertTriangle, Download
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid, Legend,
} from "recharts";

export default function SuperAdmin() {
  const [overview, setOverview] = useState(null);
  const [users, setUsers] = useState([]);
  const [q, setQ] = useState("");
  const [openUserId, setOpenUserId] = useState(null);
  const [addOpen, setAddOpen] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);

  const loadOverview = () => api.get("/admin/overview").then(r => setOverview(r.data));
  const loadUsers = (query) =>
    api.get("/admin/users", { params: query ? { q: query } : {} }).then(r => setUsers(r.data));

  useEffect(() => {
    loadOverview();
    loadUsers();
  }, []);

  // Debounced search
  useEffect(() => {
    const id = setTimeout(() => loadUsers(q), 300);
    return () => clearTimeout(id);
  }, [q]);

  const onDeleted = (id) => {
    setUsers(prev => prev.filter(u => u.id !== id));
    loadOverview();
    setConfirmDelete(null);
    toast.success("User and all their data deleted");
  };

  const onCreated = (newUser) => {
    setUsers(prev => [{ ...newUser, agents_count: 0, chats_count: 0, leads_count: 0, days_left: 0, expired: false }, ...prev]);
    loadOverview();
    setAddOpen(false);
    toast.success("User created");
  };

  const [exporting, setExporting] = useState(null);
  const exportCsv = async (kind) => {
    setExporting(kind);
    try {
      const token = localStorage.getItem("lumiar_token");
      const res = await fetch(`${API}/admin/export/${kind}.csv`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const cd = res.headers.get("Content-Disposition") || "";
      const match = cd.match(/filename="?([^";]+)"?/i);
      a.download = match ? match[1] : `ipnova_${kind}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(`${kind} exported`);
    } catch (e) {
      toast.error(`Export failed: ${e.message}`);
    } finally {
      setExporting(null);
    }
  };

  if (!overview) return <div className="p-10 text-zinc-500"><Loader2 className="w-5 h-5 animate-spin"/></div>;

  return (
    <div className="px-6 lg:px-10 py-8 max-w-7xl mx-auto" data-testid="super-admin-page">
      <div className="flex items-center gap-2 text-xs tracking-[0.2em] uppercase mb-2">
        <Crown className="w-3.5 h-3.5 text-amber-400"/>
        <span className="text-amber-400">Founder console</span>
      </div>
      <h1 className="font-display text-3xl sm:text-4xl tracking-tighter font-medium">ipnova HQ</h1>
      <p className="text-zinc-400 mt-2 text-sm">Platform-wide view of every user, every lead, every rupee.</p>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mt-8">
        <Kpi icon={Users} label="Users" value={overview.users.total} sub={`${overview.users.paid} paid · ${overview.users.trial} trial`}/>
        <Kpi icon={Bot} label="Agents" value={`${overview.agents.active}/${overview.agents.total}`}/>
        <Kpi icon={MessageSquare} label="Chats" value={overview.chats.total} sub={`${overview.chats.completed} done`}/>
        <Kpi icon={UserPlus} label="Leads" value={overview.leads.total}/>
        <Kpi icon={IndianRupee} label="Revenue" value={`₹${overview.billing.revenue_inr.toLocaleString()}`} sub={`${overview.billing.orders_paid} subs paid`} accent/>
        <Kpi icon={TrendingUp} label="MRR est" value={`₹${overview.billing.mrr_estimate_inr.toLocaleString()}`} accent/>
      </div>

      {overview.series && overview.series.length > 0 && (
        <div className="mt-6 bg-zinc-950 border border-white/10 rounded-xl p-5" data-testid="platform-trend-chart">
          <div className="flex items-center justify-between mb-3">
            <div className="text-[10px] tracking-[0.2em] uppercase text-zinc-500">Platform activity · last 14 days</div>
            <div className="text-[10px] tracking-widest uppercase text-zinc-600">signups · chats · leads</div>
          </div>
          <ResponsiveContainer width="100%" height={260}>
            <AreaChart data={overview.series} margin={{ top: 10, right: 10, left: -8, bottom: 0 }}>
              <defs>
                <linearGradient id="ah-signups" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#f59e0b" stopOpacity={0.4}/>
                  <stop offset="100%" stopColor="#f59e0b" stopOpacity={0}/>
                </linearGradient>
                <linearGradient id="ah-chats" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#60a5fa" stopOpacity={0.4}/>
                  <stop offset="100%" stopColor="#60a5fa" stopOpacity={0}/>
                </linearGradient>
                <linearGradient id="ah-leads" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#10b981" stopOpacity={0.5}/>
                  <stop offset="100%" stopColor="#10b981" stopOpacity={0}/>
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)"/>
              <XAxis dataKey="date" stroke="#52525b" tick={{ fontSize: 11, fill: "#71717a" }} tickLine={false} axisLine={false} />
              <YAxis stroke="#52525b" tick={{ fontSize: 11, fill: "#71717a" }} allowDecimals={false} tickLine={false} axisLine={false} />
              <Tooltip contentStyle={{ background: "#09090b", border: "1px solid rgba(255,255,255,0.1)", borderRadius: "10px", fontSize: "12px", padding: "8px 12px" }} cursor={{ stroke: "rgba(255,255,255,0.08)", strokeWidth: 1 }}/>
              <Legend wrapperStyle={{ fontSize: 11, color: "#a1a1aa" }}/>
              <Area type="monotone" dataKey="signups" name="Signups" stroke="#f59e0b" strokeWidth={2} fill="url(#ah-signups)" activeDot={{ r: 4 }}/>
              <Area type="monotone" dataKey="chats" name="Chats" stroke="#60a5fa" strokeWidth={2} fill="url(#ah-chats)" activeDot={{ r: 4 }}/>
              <Area type="monotone" dataKey="leads" name="Leads" stroke="#10b981" strokeWidth={2.5} fill="url(#ah-leads)" activeDot={{ r: 5 }}/>
            </AreaChart>
          </ResponsiveContainer>
        </div>
      )}

      <div className="mt-8 flex items-center gap-3 flex-wrap">
        <div className="relative flex-1 min-w-[240px]">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500 pointer-events-none"/>
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by name, email, or business…"
            className="h-11 pl-10 bg-zinc-950 border-white/10 focus:border-white/30 text-white" data-testid="admin-user-search"/>
        </div>
        <Button onClick={() => setAddOpen(true)} className="bg-white text-black hover:bg-zinc-200 h-11 px-4" data-testid="admin-add-user-btn">
          <Plus className="w-4 h-4 mr-1.5"/> Add user
        </Button>
      </div>

      <div className="mt-3 flex items-center gap-2 flex-wrap" data-testid="admin-export-row">
        <div className="text-[10px] tracking-[0.2em] uppercase text-zinc-500 mr-1">Export ·</div>
        {[
          { k: "users", label: "Users" },
          { k: "leads", label: "Leads" },
          { k: "agents", label: "Agents" },
          { k: "orders", label: "Orders" },
        ].map(({ k, label }) => (
          <Button key={k} size="sm" variant="outline"
            onClick={() => exportCsv(k)} disabled={exporting === k}
            className="h-8 bg-white/5 border-white/10 text-zinc-300 hover:bg-white/10 hover:text-white"
            data-testid={`admin-export-${k}`}>
            {exporting === k ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5"/> : <Download className="w-3.5 h-3.5 mr-1.5"/>}
            {label}.csv
          </Button>
        ))}
      </div>

      <div className="mt-4 text-xs text-zinc-500">{users.length} user{users.length !== 1 && "s"}{q && ` matching "${q}"`}</div>

      <div className="mt-3 bg-zinc-950 border border-white/10 rounded-xl overflow-hidden overflow-x-auto">
        <table className="w-full text-sm min-w-[900px]">
          <thead>
            <tr className="text-left text-[10px] tracking-[0.2em] uppercase text-zinc-500 border-b border-white/10">
              <th className="px-5 py-3 font-normal">User</th>
              <th className="px-5 py-3 font-normal">Plan</th>
              <th className="px-5 py-3 font-normal">Agents</th>
              <th className="px-5 py-3 font-normal">Chats</th>
              <th className="px-5 py-3 font-normal">Leads</th>
              <th className="px-5 py-3 font-normal">Status</th>
              <th className="px-5 py-3 font-normal">Joined</th>
              <th className="px-5 py-3 font-normal text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {users.length === 0 && (
              <tr><td colSpan={8} className="px-5 py-10 text-center text-sm text-zinc-500">No users found</td></tr>
            )}
            {users.map((u, i) => (
              <tr key={u.id} className={`border-b border-white/5 hover:bg-white/5 transition ${i % 2 ? "bg-black/20" : ""}`} data-testid={`admin-user-${u.id}`}>
                <td className="px-5 py-3 cursor-pointer" onClick={() => setOpenUserId(u.id)}>
                  <div className="flex items-center gap-3">
                    <div className="w-7 h-7 rounded-full bg-gradient-to-br from-emerald-500/30 to-emerald-500/5 border border-emerald-500/20 grid place-items-center text-xs font-display">{(u.name || "?")[0].toUpperCase()}</div>
                    <div>
                      <div className="font-medium text-white flex items-center gap-1.5">
                        {u.name}
                        {u.is_super_admin && <Crown className="w-3 h-3 text-amber-400"/>}
                      </div>
                      <div className="text-[11px] text-zinc-500">{u.email} · {u.business_name}</div>
                    </div>
                  </div>
                </td>
                <td className="px-5 py-3"><PlanBadge plan={u.plan}/></td>
                <td className="px-5 py-3 text-zinc-300">{u.agents_count}</td>
                <td className="px-5 py-3 text-zinc-300">{u.chats_count}</td>
                <td className="px-5 py-3 text-zinc-300">{u.leads_count}</td>
                <td className="px-5 py-3">
                  {u.expired ? <span className="text-red-400 text-xs">Expired</span>
                    : u.plan === "founder" || u.plan === "lifetime" ? <span className="text-amber-400 text-xs">∞</span>
                    : <span className="text-zinc-400 text-xs">{u.days_left}d</span>}
                </td>
                <td className="px-5 py-3 text-zinc-500 text-xs">{u.created_at ? new Date(u.created_at).toLocaleDateString() : "—"}</td>
                <td className="px-5 py-3 text-right">
                  <button
                    disabled={u.is_super_admin}
                    onClick={(e) => { e.stopPropagation(); setConfirmDelete(u); }}
                    className="text-zinc-500 hover:text-red-400 disabled:text-zinc-800 disabled:cursor-not-allowed p-1.5 rounded hover:bg-red-500/10"
                    title={u.is_super_admin ? "Can't delete super admin" : "Delete user"}
                    data-testid={`admin-delete-${u.id}`}>
                    <Trash2 className="w-3.5 h-3.5"/>
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {openUserId && <UserDetail userId={openUserId} onClose={() => setOpenUserId(null)} />}
      {addOpen && <AddUserModal onClose={() => setAddOpen(false)} onCreated={onCreated} />}
      {confirmDelete && <ConfirmDeleteModal user={confirmDelete} onClose={() => setConfirmDelete(null)} onDeleted={onDeleted} />}
    </div>
  );
}

function PlanBadge({ plan }) {
  const styles = {
    founder: "bg-amber-500/15 text-amber-300 border-amber-500/30",
    lifetime: "bg-amber-500/15 text-amber-300 border-amber-500/30",
    trial: "bg-zinc-500/15 text-zinc-300 border-zinc-500/30",
    monthly: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
    yearly: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
  };
  return <Badge className={`${styles[plan] || styles.trial} capitalize text-[10px]`}>{plan}</Badge>;
}

function Kpi({ icon: Icon, label, value, sub, accent }) {
  return (
    <div className={`bg-zinc-950 border ${accent ? "border-emerald-500/30" : "border-white/10"} rounded-xl p-4`} data-testid={`admin-kpi-${label}`}>
      <div className="flex items-center gap-2 text-[10px] tracking-[0.2em] uppercase text-zinc-500"><Icon className={`w-3.5 h-3.5 ${accent ? "text-emerald-400" : ""}`}/>{label}</div>
      <div className={`font-display text-3xl tracking-tighter mt-2 ${accent ? "text-emerald-400" : ""}`}>{value}</div>
      {sub && <div className="text-[10px] text-zinc-500 mt-1">{sub}</div>}
    </div>
  );
}

function AddUserModal({ onClose, onCreated }) {
  const [form, setForm] = useState({ name: "", email: "", password: "", business_name: "", plan: "trial" });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const r = await api.post("/admin/users", form);
      onCreated(r.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/85 backdrop-blur-sm grid place-items-center p-4 overflow-y-auto" onClick={onClose} data-testid="admin-add-user-modal">
      <form onSubmit={submit} onClick={(e) => e.stopPropagation()} className="bg-zinc-950 border border-white/10 rounded-2xl w-full max-w-md p-6">
        <div className="flex items-start justify-between mb-5">
          <div>
            <div className="text-xs tracking-[0.2em] uppercase text-emerald-400 mb-1">Founder · Add user</div>
            <div className="font-display text-2xl tracking-tighter">Create a new account</div>
            <div className="text-xs text-zinc-500 mt-1">Skips OTP — credentials are active immediately.</div>
          </div>
          <button type="button" onClick={onClose} className="text-zinc-500 hover:text-white"><X className="w-5 h-5"/></button>
        </div>
        <div className="space-y-3">
          <Labeled label="Name"><Input required value={form.name} onChange={set("name")} className="h-10 bg-transparent border-white/10 focus:border-white/30 text-white" data-testid="add-name"/></Labeled>
          <Labeled label="Email"><Input type="email" required value={form.email} onChange={set("email")} className="h-10 bg-transparent border-white/10 focus:border-white/30 text-white" data-testid="add-email"/></Labeled>
          <Labeled label="Password"><Input type="text" required minLength={6} value={form.password} onChange={set("password")} className="h-10 bg-transparent border-white/10 focus:border-white/30 text-white font-mono" data-testid="add-password"/></Labeled>
          <Labeled label="Business name"><Input value={form.business_name} onChange={set("business_name")} className="h-10 bg-transparent border-white/10 focus:border-white/30 text-white" data-testid="add-business"/></Labeled>
          <Labeled label="Plan">
            <select value={form.plan} onChange={set("plan")} className="w-full h-10 bg-transparent border border-white/10 rounded-md px-3 focus:border-white/30 outline-none text-white text-sm" data-testid="add-plan">
              <option value="trial" className="bg-zinc-950">Trial (14 days)</option>
              <option value="monthly" className="bg-zinc-950">Monthly (30 days)</option>
              <option value="yearly" className="bg-zinc-950">Yearly (365 days)</option>
              <option value="lifetime" className="bg-zinc-950">Lifetime (free forever)</option>
            </select>
          </Labeled>
        </div>
        <div className="mt-5 flex gap-2">
          <Button type="button" variant="ghost" onClick={onClose} className="flex-1 text-zinc-400 hover:bg-white/5" data-testid="add-cancel">Cancel</Button>
          <Button type="submit" disabled={busy} className="flex-1 bg-white text-black hover:bg-zinc-200" data-testid="add-submit">
            {busy ? <Loader2 className="w-4 h-4 animate-spin"/> : "Create user"}
          </Button>
        </div>
      </form>
    </div>
  );
}

function Labeled({ label, children }) {
  return (
    <div>
      <div className="text-[10px] tracking-widest uppercase text-zinc-500 mb-1.5">{label}</div>
      {children}
    </div>
  );
}

function ConfirmDeleteModal({ user, onClose, onDeleted }) {
  const [busy, setBusy] = useState(false);
  const [confirmText, setConfirmText] = useState("");

  const submit = async () => {
    if (confirmText !== user.email) { toast.error("Type the email to confirm"); return; }
    setBusy(true);
    try {
      await api.delete(`/admin/users/${user.id}`);
      onDeleted(user.id);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/85 backdrop-blur-sm grid place-items-center p-4" onClick={onClose} data-testid="admin-confirm-delete">
      <div onClick={(e) => e.stopPropagation()} className="bg-zinc-950 border border-red-500/30 rounded-2xl w-full max-w-md p-6">
        <div className="flex items-start gap-3 mb-4">
          <div className="w-10 h-10 rounded-lg bg-red-500/10 border border-red-500/30 grid place-items-center shrink-0">
            <AlertTriangle className="w-5 h-5 text-red-400"/>
          </div>
          <div>
            <div className="font-display text-xl tracking-tighter">Delete this user?</div>
            <div className="text-sm text-zinc-400 mt-1">
              <span className="text-white">{user.name}</span> ({user.email}) and ALL their data —
              agents, chats, leads, orders — will be permanently deleted. This cannot be undone.
            </div>
          </div>
        </div>
        <div className="text-[10px] tracking-widest uppercase text-zinc-500 mb-1.5 mt-3">Type the email to confirm</div>
        <Input value={confirmText} onChange={(e) => setConfirmText(e.target.value)} placeholder={user.email}
          className="h-10 bg-transparent border-white/10 focus:border-red-500/40 text-white font-mono" data-testid="delete-confirm-input"/>
        <div className="mt-5 flex gap-2">
          <Button variant="ghost" onClick={onClose} className="flex-1 text-zinc-400 hover:bg-white/5" data-testid="delete-cancel">Cancel</Button>
          <Button onClick={submit} disabled={busy || confirmText !== user.email}
            className="flex-1 bg-red-500 text-white hover:bg-red-600 disabled:opacity-40" data-testid="delete-confirm">
            {busy ? <Loader2 className="w-4 h-4 animate-spin"/> : <><Trash2 className="w-4 h-4 mr-1.5"/> Delete user</>}
          </Button>
        </div>
      </div>
    </div>
  );
}

function UserDetail({ userId, onClose }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get(`/admin/users/${userId}`).then(r => setData(r.data));
  }, [userId]);

  return (
    <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm grid place-items-center p-4 overflow-y-auto" onClick={onClose} data-testid="admin-user-detail">
      <div onClick={(e) => e.stopPropagation()} className="bg-zinc-950 border border-white/10 rounded-2xl w-full max-w-3xl my-8">
        {!data ? <div className="p-10 text-zinc-500"><Loader2 className="w-5 h-5 animate-spin"/></div> : (
          <>
            <div className="p-6 border-b border-white/10 flex items-start justify-between">
              <div>
                <div className="text-xs tracking-[0.2em] uppercase text-zinc-500">User</div>
                <div className="font-display text-2xl tracking-tighter mt-1">{data.user.name}</div>
                <div className="text-xs text-zinc-500 mt-1 flex items-center gap-3 flex-wrap">
                  <span className="flex items-center gap-1"><Mail className="w-3 h-3"/> {data.user.email}</span>
                  <span>{data.user.business_name}</span>
                  <PlanBadge plan={data.user.plan}/>
                </div>
              </div>
              <button onClick={onClose} className="text-zinc-500 hover:text-white"><X className="w-5 h-5"/></button>
            </div>
            <div className="p-6 grid sm:grid-cols-3 gap-3 border-b border-white/10">
              <Mini label="Agents" value={data.automations.length}/>
              <Mini label="Leads" value={data.leads.length}/>
              <Mini label="Orders" value={data.orders.length}/>
            </div>

            <Section title={`Agents (${data.automations.length})`}>
              {data.automations.length === 0 ? <Empty>No agents yet</Empty>
                : data.automations.map(a => (
                  <div key={a.id} className="bg-black/40 border border-white/5 rounded-md p-3">
                    <div className="text-sm font-medium">{a.name}</div>
                    <div className="text-[11px] text-zinc-500 font-mono">/c/{a.slug}</div>
                  </div>
                ))}
            </Section>

            <Section title={`Leads (${data.leads.length})`}>
              {data.leads.length === 0 ? <Empty>No leads yet</Empty>
                : data.leads.map(l => (
                  <div key={l.id} className="bg-black/40 border border-white/5 rounded-md p-3">
                    <div className="flex items-center justify-between">
                      <div className="text-sm font-medium">{l.contact_name}</div>
                      <Badge className="bg-white/5 text-zinc-300 border-white/10 text-[10px] capitalize">{l.stage}</Badge>
                    </div>
                    <div className="text-[11px] text-zinc-500 font-mono flex items-center gap-1 mt-0.5"><Phone className="w-2.5 h-2.5"/>{l.contact_phone || "—"}</div>
                    {Object.keys(l.captured_data || {}).length > 0 && (
                      <div className="mt-2 text-[11px] text-zinc-400 space-y-0.5">
                        {Object.entries(l.captured_data).slice(0, 5).map(([k, v]) => (
                          <div key={k}><span className="text-zinc-500">{k}:</span> {String(v)}</div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
            </Section>

            <Section title={`Orders (${data.orders.length})`} last>
              {data.orders.length === 0 ? <Empty>No orders</Empty>
                : data.orders.map(o => (
                  <div key={o.id} className="bg-black/40 border border-white/5 rounded-md p-3 flex items-center justify-between">
                    <div>
                      <div className="font-mono text-[11px] text-zinc-400">{o.razorpay_order_id}</div>
                      <div className="text-[11px] text-zinc-600">{new Date(o.created_at).toLocaleString()}</div>
                    </div>
                    <div className="text-right">
                      <div className="font-display text-base">₹{(o.amount || 0).toLocaleString()}</div>
                      <Badge className="bg-emerald-500/15 text-emerald-300 border-emerald-500/30 text-[10px] capitalize">{o.status}</Badge>
                    </div>
                  </div>
                ))}
            </Section>
          </>
        )}
      </div>
    </div>
  );
}

function Section({ title, children, last }) {
  return (
    <div className={`p-6 space-y-2 ${last ? "" : "border-b border-white/10"}`}>
      <div className="text-[10px] tracking-[0.2em] uppercase text-zinc-500">{title}</div>
      <div className="space-y-2 max-h-72 overflow-y-auto">{children}</div>
    </div>
  );
}
function Mini({ label, value }) {
  return (
    <div className="bg-black/40 border border-white/5 rounded-md p-3">
      <div className="text-[10px] tracking-widest uppercase text-zinc-500">{label}</div>
      <div className="font-display text-2xl tracking-tighter">{value}</div>
    </div>
  );
}
function Empty({ children }) { return <div className="text-xs text-zinc-500 py-3">{children}</div>; }
